import { CombinedGraphQLErrors } from "@apollo/client/errors";
import type { IntlShape } from "react-intl";
import {
  validationResultMessage,
  type ValidationIssueLike,
} from "@/lib/graphql-error";
import { userErrorMessage } from "@/lib/user-error";

// What a failed company mutation is allowed to tell the admin.
//
// genesis refuses some writes on purpose — the last admin may be neither removed nor demoted
// (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §6), and a person may not be added twice. Those refusals are the difference between
// "the platform is broken" and "here is what to do instead", so they must not collapse into the
// generic "Something went wrong".
//
// HOW PRECISELY THEY CAN BE READ OFF THE WIRE DIFFERS PER REFUSAL, so there are two mechanisms here
// and the caller picks by what genesis actually sends.
//
//   - By CODE, when genesis raised a CATALOGUED message. genesis's exception resolver copies
//     ServiceException.getErrorMessage().code() into extensions.code, so a catalogued refusal is
//     identifiable exactly, and a different refusal reaching the same operation later cannot quietly
//     inherit this one's wording. GENESIS001241 (too many contact persons) is such a refusal.
//   - By CLASSIFICATION, when it was not. The older refusals are raised as
//     InputDataValidationException built with ErrorMessage.of(message) — no code — so extensions
//     carry only errorId/correlationId, and the message itself names raw UUIDs in English, which
//     @adr SPARK-0010-no-raw-server-errors-in-ui@1 forbids rendering. All that is left is graphql-java's classification, and per
//     operation that is just enough: after the admin gate, the only InputDataValidationException
//     reachable in removeCompanyMember and setCompanyMemberRole is the last-admin refusal, and in
//     addCompanyMember it is the duplicate.
//
// Either way the caller names the refusal ITS operation can produce; nothing here guesses.

/** graphql-java writes the resolver's ErrorClassification here (DefaultDataFetcherExceptionResolver). */
const VALIDATION = "VALIDATION";

/**
 * genesis's literal for PermissionDeniedException. Matched on the message because the resolver
 * does NOT classify it: PermissionDeniedException extends ServiceException, not Spring's
 * AccessDeniedException, so it falls through to the INTERNAL_ERROR branch and arrives
 * indistinguishable from a crash by classification alone. Reported as a genesis defect; until it
 * is fixed this literal is the only signal, and it matters because spark's admin gate is a stale
 * copy of the membership row — being told to reload beats being told nothing.
 */
const PERMISSION_DENIED = "permission denied";

function graphQlErrors(error: unknown): readonly { message?: string | null; extensions?: Readonly<Record<string, unknown>> | null }[] {
  return CombinedGraphQLErrors.is(error) ? error.errors : [];
}

/** True when genesis refused the input itself, as opposed to failing. */
export function isInputRefusal(error: unknown): boolean {
  return graphQlErrors(error).some((e) => e.extensions?.["classification"] === VALIDATION);
}

/**
 * True when genesis answered with this catalogued error code.
 *
 * Exact match on `extensions.code`, which only ServiceException subclasses carry. This is the
 * precise half of the pair described at the top: it identifies ONE refusal rather than "whatever
 * VALIDATION this operation can currently reach", so it stays correct when genesis grows a second
 * refusal on the same mutation.
 */
export function hasErrorCode(error: unknown, code: string): boolean {
  return graphQlErrors(error).some((e) => e.extensions?.["code"] === code);
}

/**
 * True when genesis catalogued this refusal at all, whatever the code.
 *
 * The signal that a classification-only match must stand down: a coded refusal has an exact
 * identity, and answering it with "whatever VALIDATION this operation used to be able to reach"
 * is how a newly added rule inherits an older rule's wording.
 */
function hasAnyErrorCode(error: unknown): boolean {
  return graphQlErrors(error).some((e) => typeof e.extensions?.["code"] === "string");
}

/** True when genesis says the caller does not administer this company. */
export function isPermissionRefusal(error: unknown): boolean {
  return graphQlErrors(error).some((e) => (e.message ?? "").trim().toLowerCase() === PERMISSION_DENIED);
}

/**
 * What THIS operation's deliberate refusal means, already localized — and how to recognize it.
 *
 * A bare string is matched by classification: use it only where the operation has exactly one
 * reachable VALIDATION refusal. The object form is matched on genesis's catalogued
 * `extensions.code` and is preferred wherever a code exists, because it stays right when a second
 * refusal appears on the same mutation.
 */
export type CompanyRefusal = string | { readonly code: string; readonly message: string };

/**
 * The refusals one operation can produce, tried in order.
 *
 * A LIST rather than a single value because genesis grew a second catalogued refusal on the
 * membership mutations (GENESIS001242, "that person's account is not active") beside the last-admin one.
 * With a single bare string the classification match would have answered BOTH with the last-admin
 * wording — telling the admin to promote somebody else when promoting is exactly what just failed.
 * Code-matched entries are therefore tried first, and a bare string is only ever a last resort.
 */
export type CompanyRefusals = CompanyRefusal | readonly CompanyRefusal[];

/**
 * A localized, actionable message for a failed company mutation.
 *
 * @param refusal what this operation's deliberate refusal means. Null when the operation has no
 *   refusal spark can name, in which case a refusal reads as any other failure rather than being
 *   mislabelled — the honest answer, since naming the wrong rule is worse than naming none.
 */
export function companyAdminErrorMessage(
  intl: IntlShape,
  error: unknown,
  refusal: CompanyRefusals | null,
): string {
  if (isPermissionRefusal(error)) {
    return intl.formatMessage({
      id: "company.admin.error.notAdmin",
      defaultMessage:
        "You no longer administer this company. Reload the page to see what you can still do.",
    });
  }
  if (refusal != null) {
    const candidates = Array.isArray(refusal) ? refusal : [refusal as CompanyRefusal];
    // Coded first, ALWAYS. A bare string matches any VALIDATION on the operation, so trying it
    // before the codes would let the loosest entry swallow a refusal that genesis named exactly.
    const coded = candidates.filter((each): each is Exclude<CompanyRefusal, string> =>
      typeof each !== "string",
    );
    for (const each of coded) {
      if (hasErrorCode(error, each.code)) return each.message;
    }
    // A bare string is only right where the refusal is genuinely UNNAMED. If genesis catalogued it
    // — with any code, including one this operation has never heard of — that code is the truth and
    // the loose entry would put the wrong rule's wording on it. Fall through to the generic message,
    // which at least does not tell the admin to do the wrong thing.
    const loose = candidates.find((each) => typeof each === "string");
    if (loose != null && isInputRefusal(error) && !hasAnyErrorCode(error)) {
      return loose as string;
    }
  }
  return userErrorMessage(intl, error);
}

// ---------------------------------------------------------------------------
// The OTHER way a company write can be refused: a ValidateCompanyResult payload rather than a
// thrown error. Same rule as everything above — the admin is told what to do, never what genesis
// called it internally.
// ---------------------------------------------------------------------------

/**
 * How the company form labels each field, so a validation message names the field the way the
 * screen does and the admin knows where to look. Reused from the form rather than re-worded, so
 * the two can never drift apart.
 *
 * The keys are the `field` values genesis puts on a `ValidationIssue`, which are input property
 * names (`CompanyController.validateData` builds them from "name", "summary", "employeeCount").
 * A field absent from this table has no label, and an issue about it therefore has no message —
 * printing the raw property name would be the leak in a thinner disguise.
 */
const FIELD_LABEL_ID: Readonly<Record<string, string>> = {
  name: "company.admin.field.name",
  industry: "company.admin.field.industry",
  employeeCount: "company.admin.field.employeeCount",
  summary: "company.admin.field.summary",
};

/**
 * What each `ValidationError` means for the admin, as a sentence about a named field.
 *
 * Keyed on the enum genesis actually declares — IS_MANDATORY / FORMAT_ERROR / ALREADY_EXISTS /
 * OTHER — and OTHER is deliberately absent: the schema defines it as "human-readable details can
 * be found the message field", so an OTHER with no message is genesis saying it has nothing to
 * add, and inventing a sentence for it would be putting words in its mouth. It falls through to
 * the caller's generic message, which is the honest answer.
 *
 * A per-(field, error) matrix was the alternative and is not built: it would mean writing copy for
 * pairs genesis has never sent, which is invention dressed as thoroughness. A label plus a rule
 * composes to a sentence that is both localized and actionable, for every pair that can arise.
 */
const ERROR_MESSAGE_ID: Readonly<Record<string, string>> = {
  IS_MANDATORY: "company.validation.isMandatory",
  FORMAT_ERROR: "company.validation.formatError",
  ALREADY_EXISTS: "company.validation.alreadyExists",
};

function companyIssueCopy(intl: IntlShape, issue: ValidationIssueLike): string | null {
  const labelId = FIELD_LABEL_ID[issue.field?.trim() ?? ""];
  const messageId = ERROR_MESSAGE_ID[issue.error?.trim() ?? ""];
  // BOTH halves or nothing. A known rule about an unknown field would have to name the field
  // somehow, and the only name to hand is genesis's property name.
  if (!labelId || !messageId) return null;
  return intl.formatMessage(
    { id: messageId },
    { field: intl.formatMessage({ id: labelId }) },
  );
}

/**
 * A localized, actionable message for a refused company write that came back as a
 * `Validate…Result` payload.
 *
 * @param fallback what to say when the payload holds nothing a user could act on — an unknown
 *   field, an `OTHER` with no message, or no issues at all.
 */
export function companyValidationMessage(
  intl: IntlShape,
  result: Parameters<typeof validationResultMessage>[0],
  fallback: string,
): string {
  return validationResultMessage(result, fallback, (issue) => companyIssueCopy(intl, issue));
}
